<script lang="ts">
  import { getContext } from 'svelte';
  import { field, form } from 'svelte-forms';
  import { required } from 'svelte-forms/validators';
  import OperationCompleteModal from '../../../etc/modal/invitation/umobiled/OperationCompleteModal.svelte';
  import RepositoryFactory, { INVITATION_UMOBILED } from '../../../repositories/RepositoryFactory';
  import { HandleMnpPortinActivationResult } from '../../../repositories/invitationumobiled';
  import { isValidMnpNo, isValidMnpTelNo, isValidSimSerialNo } from './Validator';

  const { open, close }: any = getContext('simple-modal');

  // input項目
  const mnpTelno = field('mnpTelno', '', [required(), isValidMnpTelNo]);
  const simSerialNo = field('simSerialNo', '', [required(), isValidSimSerialNo]);
  const mnpReserveNo = field('mnpReserveNo', '', [required(), isValidMnpNo]);

  const myForm = form(mnpTelno, simSerialNo, mnpReserveNo);

  const repository = RepositoryFactory[INVITATION_UMOBILED];

  let errorMessage: string | null = null;

  const handleConfirm = async () => {
    errorMessage = null;
    let isFormValid = false;

    await myForm.validate().then(() => {
      isFormValid = $myForm.valid;
    });
    if (!isFormValid) {
      return;
    }

    repository
      .handleMnpPortinActivation({
        mnpTelno: $mnpTelno.value,
        simSerialNo: $simSerialNo.value,
        mnpReserveNo: $mnpReserveNo.value
      })
      .then((response: HandleMnpPortinActivationResult) => {
        if (response && !response.error) {
          showCompleteModal();
        } else {
          errorMessage = 'MNP電話番号またはSIM製造番号に誤りがあります。確認の上、再度開通ボタンを押下ください。';
        }
      })
      .catch((error) => {
        errorMessage =
          '予期せぬエラーが発生しました。MNP電話番号、SIM製造番号を確認の上、再度開通ボタンを押下ください。';
      });
  };

  const showCompleteModal = () => {
    open(
      OperationCompleteModal,
      {
        modalTitle: '開通処理を受け付けました',
        modalMessage: '開通処理を受け付けました、メールが届くまでしばらくお待ちください。',
        handleCloseEvent: () => {
          close();
          location.reload();
        }
      },
      {
        closeButton: false,
        closeOnEsc: false,
        closeOnOuterClick: false
      }
    );
  };

  const handleSubmit = (e: Event) => {
    e.preventDefault();
  };
</script>

<!-- 本体 -->
<div class="flex flex-col justify-between items-center login-background">
  <header class="container top-0 z-0">
    <div class="justify-between items-center" style="height:56px">
      <img style="height:36px" class="object-none w-full" src="/image/unext_communication_logo.svg" alt="logo" />
    </div>
  </header>

  <main class="container mt-10 mb-auto">
    <div class="flex justify-center text-2xl">U-mobile(d)移管特別オファー申込ページ</div>

    <div class="flex justify-center items-center mt-10">
      <div class="flex justify-center items-center info-container m-2 mb:font-sm">
        <ul class="list-none space-y-2">
          <li>お手元に届いたSIMをご準備の上、以下の手順に従ってご入力ください。</li>
          <ul class="list-disc list-inside space-y-1">
            <li>必要情報をフォームに入力し、開通ボタンを押下してください。</li>
            <li>弊社での処理完了後、ご登録のメールアドレスへ開通完了のご連絡をお送りします。</li>
            <li>メールが届いたら、お手持ちの端末にSIMを挿入し、APN設定を行ってください。</li>
          </ul>
        </ul>
      </div>
    </div>

    <form on:submit={handleSubmit}>
      <div class="flex justify-center items-center mt-10">
        <div class="content-center shadow-sm p-5 form-background mb-10">
          <div class="flex flex-col items-center">
            <div class="form-container">
              <!-- MNP電話番号 -->
              <div class="mt-4">
                <label for="mnpTelno" class="form-input-label">MNP電話番号（ハイフンなし）</label>
                <input
                  type="text"
                  id="mnpTelno"
                  maxlength="14"
                  autocomplete="off"
                  placeholder="例) 08011112222"
                  bind:value={$mnpTelno.value}
                />
                {#if $myForm.hasError('mnpTelno.required')}
                  <p class="helper-text">MNP電話番号を入力してください</p>
                {/if}
                {#if $myForm.hasError('mnpTelno.invalid_mnp_tel_no')}
                  <p class="helper-text">MNP電話番号が間違えています。正しい電話番号を入力ください</p>
                {/if}
              </div>

              <!-- SIM製造番号 -->
              <div class="mt-4">
                <label for="simSerialNo" class="form-input-label">SIM製造番号</label>
                <input
                  type="text"
                  id="simSerialNo"
                  maxlength="15"
                  autocomplete="off"
                  placeholder="半角英数字15文字"
                  bind:value={$simSerialNo.value}
                />
                {#if $myForm.hasError('simSerialNo.required')}
                  <p class="helper-text">SIM製造番号を入力してください</p>
                {/if}
                {#if $myForm.hasError('simSerialNo.invalid_sim_serial_no')}
                  <p class="helper-text">SIM製造番号が間違えています。正しいSIM製造番号を入力ください</p>
                {/if}
              </div>

              <!-- MNP予約番号 -->
              <div class="mt-4">
                <label for="mnpReserveNo" class="form-input-label">MNP予約番号</label>
                <input
                  type="text"
                  id="mnpReserveNo"
                  maxlength="10"
                  autocomplete="off"
                  placeholder="半角数字10文字"
                  bind:value={$mnpReserveNo.value}
                />
                {#if $myForm.hasError('mnpReserveNo.required')}
                  <p class="helper-text">MNP予約番号を入力してください</p>
                {/if}
                {#if $myForm.hasError('mnpReserveNo.invalid_mnp_no')}
                  <p class="helper-text">MNP予約番号が間違えています。正しい予約番号を入力ください</p>
                {/if}
              </div>

              <div class="flex justify-center mt-10">
                <button class="tracking-widest form-button h-12 w-full" on:click={handleConfirm}>開通</button>
              </div>
              <div class="flex justify-center items-center mt-2 mb-5">
                {#if errorMessage}
                  <p class="helper-text">{errorMessage}</p>
                {/if}
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </main>

  <footer class="container" />
</div>

<style lang="postcss">
  .login-background {
    background-color: white;
  }

  .form-input-label {
    @apply text-base font-normal text-gray-900;
  }

  input[type='text'] {
    font-size: 14px;
    color: rgba(0, 10, 23, 1);
    background-color: rgba(1, 6, 13, 0.06);
    @apply p-3 mt-1 appearance-none min-h-12 w-full focus:ring-0 focus:ring-blue-200 focus:border-transparent;
  }

  .info-container {
    width: 720px;
    @apply text-base font-normal text-gray-900;
  }

  .form-background {
    width: 720px;
    background-color: rgba(0, 0, 0, 0.02);
  }

  .form-container {
    width: 352px;
  }

  @media screen and (max-width: 640px) {
    .info-container {
      width: 100%;
    }

    .form-background {
      width: 100%;
    }
    .form-container {
      width: 100%;
    }
  }

  .form-button {
    @apply text-center text-sm font-medium rounded-none text-white bg-[#0fb9f5];
  }

  .form-button:hover {
    text-decoration: none;
    -webkit-filter: brightness(1.1);
    filter: brightness(1.1);
  }

  .helper-text {
    @apply text-xs text-red-500  mt-2;
  }
</style>
